import { ReactElement } from "react";
import { Box } from "@twilio-paste/core/box";
import {
  Pagination,
  PaginationEllipsis,
  PaginationArrow,
  PaginationItems,
  PaginationNumber,
  PaginationNumbers,
} from "@twilio-paste/core/pagination";
import { Text } from "@twilio-paste/core/text";
import { constants, copy, types } from "./definitions";

const CompositePagination = ({
  currentPage,
  onPaginate,
  pageCount,
}: types.CompositePaginationProps): ReactElement => {
  const { firstPage, pageBuffer, maxEllipsisPageCount } =
    constants.paginationDefaults;

  const shouldRenderPreviousEllipsis =
    pageCount > maxEllipsisPageCount && currentPage > pageBuffer;
  const shouldRenderNextEllipsis =
    pageCount > maxEllipsisPageCount && currentPage <= pageCount - pageBuffer;
  const shouldRenderBookendNumbers =
    shouldRenderPreviousEllipsis || shouldRenderNextEllipsis;

  const handlePaginate = (
    action: types.PaginationNavigation,
    page?: number
  ): void => {
    switch (action) {
      case types.PaginationNavigation.back: {
        handleGoToPreviousPage();
        break;
      }
      case types.PaginationNavigation.forward: {
        handleGoToNextPage();
        break;
      }
      case types.PaginationNavigation.toPage: {
        // PaginationNumber does not have a 'disable' prop, so this will
        // disallow wasted fetch calls if the current page is clicked
        if (page && page !== currentPage) {
          handleGoToPage(page);
        }
        break;
      }
    }
  };

  const handleGoToNextPage = (): void => {
    const newPage = Math.min(currentPage + 1, pageCount);
    onPaginate(newPage);
  };

  const handleGoToPreviousPage = (): void => {
    const newPage = Math.max(currentPage - 1, 1);
    onPaginate(newPage);
  };

  const handleGoToPage = (page: number): void => {
    onPaginate(page);
  };

  /**
   * Details on how to use PaginationEllipsis from Paste
   * https://paste.twilio.design/components/pagination#using-paginationellipsis
   */
  const determinePagesToRender = (): number[] => {
    // Show pages 1 ... two surrounding current ... last
    if (shouldRenderNextEllipsis && shouldRenderPreviousEllipsis) {
      return [currentPage - 1, currentPage, currentPage + 1];
    }

    // Show pages 1-5 ... last
    if (shouldRenderNextEllipsis) {
      return [2, 3, 4, 5];
    }

    // Show pages 1 ... last 5 pages
    if (shouldRenderPreviousEllipsis) {
      const endPages: number[] = [];

      for (
        let endPageNumber = pageCount - 1;
        endPageNumber < pageCount && endPageNumber >= pageCount - pageBuffer;
        endPageNumber--
      ) {
        endPages.push(endPageNumber);
      }

      return endPages.reverse();
    }

    // Page count is less than 8, show all pages
    return Array.from({ length: pageCount }, (_, index) => index + 1);
  };

  const pages = determinePagesToRender();

  return (
    <Box>
      <Box display="flex" justifyContent="center" marginTop="space70">
        <Pagination label={copy.pagination.controlsLabel}>
          <PaginationItems>
            <PaginationArrow
              label={copy.pagination.goBackLabel}
              variant="back"
              onClick={(): void =>
                handlePaginate(types.PaginationNavigation.back)
              }
              disabled={currentPage === 1}
            />

            <PaginationNumbers>
              {shouldRenderBookendNumbers && (
                <PaginationNumber
                  label={copy.pagination.goToPageLabel(firstPage)}
                  isCurrent={firstPage === currentPage}
                  onClick={(): void =>
                    handlePaginate(types.PaginationNavigation.toPage, firstPage)
                  }
                >
                  {firstPage}
                </PaginationNumber>
              )}

              {shouldRenderPreviousEllipsis && (
                <PaginationEllipsis
                  label={copy.pagination.ellipsisPreviousLabel}
                />
              )}

              {pages.map((page) => (
                <PaginationNumber
                  label={copy.pagination.goToPageLabel(page)}
                  isCurrent={page === currentPage}
                  onClick={(): void =>
                    handlePaginate(types.PaginationNavigation.toPage, page)
                  }
                  key={page}
                >
                  {page}
                </PaginationNumber>
              ))}

              {shouldRenderNextEllipsis && (
                <PaginationEllipsis label={copy.pagination.ellipsisNextLabel} />
              )}

              {shouldRenderBookendNumbers && (
                <PaginationNumber
                  label={copy.pagination.goToPageLabel(pageCount)}
                  isCurrent={pages.length - pageCount === currentPage}
                  onClick={(): void =>
                    handlePaginate(types.PaginationNavigation.toPage, pageCount)
                  }
                >
                  {pageCount}
                </PaginationNumber>
              )}
            </PaginationNumbers>

            <PaginationArrow
              label={copy.pagination.goForwardLabel}
              variant="forward"
              onClick={(): void =>
                handlePaginate(types.PaginationNavigation.forward)
              }
              disabled={currentPage === pageCount}
            />
          </PaginationItems>
        </Pagination>
      </Box>
      <Box display="flex" justifyContent="center">
        <Text as="p" color="colorTextWeak">
          {currentPage} of {pageCount}
        </Text>
      </Box>
    </Box>
  );
};

export default CompositePagination;
